import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, Download, Mail, Send, Loader2, Newspaper, Shield } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/usePageMeta";

export default function PressRelease() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [winner, setWinner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const { user } = useAuth();
  const asAdmin = !!(user?.is_admin && user.winner_slug !== slug);
  const poss = winner?.name ? `${winner.name}'s` : "this winner's";
  usePageMeta(
    { title: `Press Release${winner?.show_name && winner?.name ? ` — ${winner.name}` : ""} | #BlackGirlJoyChallenge`,
      description: "A ready-to-send press release for a 2026 #BlackGirlJoyChallenge winner." },
    [winner],
  );

  useEffect(() => {
    (async () => {
      try {
        const [pr, w] = await Promise.all([
          api.get(`/winners/${slug}/press-release`),
          api.get(`/winners/${slug}`),
        ]);
        setText(pr.data.text || null);
        setWinner(w.data);
      } catch {
        toast.error("Could not load this press release.");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const copy = () => {
    navigator.clipboard.writeText(text);
    toast.success("Press release copied!");
  };

  const downloadTxt = () => {
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "blackgirljoy-press-release.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const emailCopy = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/winners/${slug}/send-press-release`);
      toast.success(`Press release sent to ${data.to[0]} (copy to Southern Black Girls).`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send the press release right now. Please try again shortly.");
    } finally {
      setSending(false);
    }
  };

  const pitchNewsroom = () => {
    const to = window.prompt("Enter the newsroom / reporter email you want to pitch:");
    if (!to) return;
    const subject = "Story idea: Local youth leader named a #BlackGirlJoyChallenge winner";
    const body = `Hello,\n\nI wanted to share some good news from our community. The full press release is below, and a photo is attached.\n\n${
      winner?.landing_url ? `More about the project: ${winner.landing_url}\n\n` : ""
    }${text}`;
    window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteNav />
      <div className="mx-auto w-full max-w-4xl px-5 py-10">
        {asAdmin && (
          <div data-testid="admin-viewing-bar" className="mb-4 inline-flex items-center gap-2 rounded-full bg-pt-purple px-4 py-1.5 text-xs font-bold text-white">
            <Shield className="h-3.5 w-3.5" /> Signed in as ADMIN ({user.email}) — viewing {poss} press release
          </div>
        )}
        <button
          onClick={() => navigate(`/bgjcpt/${slug}`)}
          data-testid="pr-back-btn"
          className="mb-4 flex items-center gap-1.5 text-sm font-semibold text-sbg-raspberry hover:underline"
        >
          <ArrowLeft className="h-4 w-4" /> {asAdmin ? `Back to ${poss} page` : "Back to my page"}
        </button>
        <h1 className="font-display text-3xl font-black tracking-tight text-sbg-burgundy sm:text-4xl" data-testid="pr-heading">
          {asAdmin ? `${winner?.name || "This winner"}'s personal press release` : "Your personal press release"}
        </h1>
        <p className="mt-2 text-sbg-burgundy/70">
          Send this to local newspapers, TV, radio, your school, and your district. It's built from the details on your page.
        </p>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-sbg-raspberry" />
          </div>
        ) : !text ? (
          <div data-testid="pr-pending" className="mt-8 rounded-3xl bg-white p-10 text-center ring-1 ring-sbg-raspberry/10">
            <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-sbg-blush text-sbg-fuchsia"><Newspaper className="h-7 w-7" /></span>
            <h2 className="mt-4 font-display text-lg font-bold text-sbg-burgundy md:text-xl">Your press release is being prepared</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-sbg-burgundy/70">
              Our team writes and reviews a personal press release for every winner. Once it's approved, we'll email a copy to {winner?.email ? "you" : "the winner"} and it will appear here.
            </p>
            {asAdmin && (
              <Link to="/admin/bgjcpt" className="mt-5 inline-block rounded-full bg-pt-purple px-5 py-2.5 text-sm font-bold text-white">Review it in the admin dashboard</Link>
            )}
          </div>
        ) : (
          <>
            <div className="mt-7 flex flex-wrap gap-3">
              <button data-testid="pr-copy-btn" onClick={copy} className="inline-flex items-center gap-2 rounded-full bg-sbg-raspberry px-5 py-2.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5">
                <Copy className="h-4 w-4" /> Copy
              </button>
              <button data-testid="pr-download-btn" onClick={downloadTxt} className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15 transition-transform hover:-translate-y-0.5">
                <Download className="h-4 w-4" /> Download .txt
              </button>
              <button data-testid="pr-email-copy-btn" onClick={emailCopy} disabled={sending} className="inline-flex items-center gap-2 rounded-full bg-sbg-turquoise px-5 py-2.5 text-sm font-bold text-sbg-burgundy transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email me a copy
              </button>
              <button data-testid="pr-pitch-btn" onClick={pitchNewsroom} className="inline-flex items-center gap-2 rounded-full bg-pt-purple px-5 py-2.5 text-sm font-bold text-white transition-transform hover:-translate-y-0.5">
                <Newspaper className="h-4 w-4" /> Pitch a newsroom
              </button>
            </div>

            <div className="mt-3 rounded-2xl border border-pt-lavender/40 bg-pt-lavender/10 p-4 text-xs text-pt-purple">
              <strong>How sending works:</strong> “Email a copy” sends the release to your inbox (with a copy to
              the SBG press team); replies go to your media contact and pr@southernblackgirls.org. To pitch a reporter directly, “Pitch a newsroom” opens your own email app with
              everything pre-filled, so it comes from you — the best way to get picked up.
            </div>

            <article
              data-testid="pr-text"
              className="mt-6 whitespace-pre-wrap rounded-3xl bg-white p-8 font-mono text-sm leading-relaxed text-sbg-burgundy shadow-[0_12px_30px_rgba(173,28,93,0.1)] ring-1 ring-sbg-raspberry/10 sm:p-10"
            >
              {text}
            </article>

            <div className="mt-8 rounded-3xl bg-sbg-burgundy p-8 text-sbg-blush">
              <h2 className="font-display text-xl font-bold text-white">How to pitch your local news</h2>
              <ol className="mt-4 list-decimal space-y-2 pl-5 text-sm">
                <li>Find the “news tips” or “submit a story” email on a local outlet's website.</li>
                <li>Attach your framed photo — stories with photos get picked up more often.</li>
                <li>Include your winner page link so reporters can see your project.</li>
                <li>Think local: your city paper, local TV and radio, school or district, church bulletin.</li>
                <li>Follow up once after a few days. Short and friendly is best.</li>
              </ol>
            </div>
          </>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
